import React, {useEffect, useState} from "react";
import {VsiBubble, VsiH4, VsiH5} from "../../../styled/App.styled";
import {useTitleFromComponent} from "../../../utils/hooks";
import Grid from "../../../components/Grid";
import {Card, CardHeader, CardBody, Col, Row} from "reactstrap";
import { useVsiAppContext} from "../../../stores/VsiApp";
import {enterpriseDatabaseSummaryDefs} from "../../../utils/gridColumnDefs/databases/enterpriseDatabaseSummaryDefs";
import {useNavigate, useSearchParams} from "react-router-dom";
import Stockchart from "../../../components/Stockchart";
import {getReport} from "../../../utils/Verbs";
import {formatStockChartConfig} from "../../../utils/Helpers";
import PieGrid from "../../../components/PieGrid";
import { databaseTypesColors } from "../../../utils/chartUtils";

export default function DatabaseSummary({title}) {

    useTitleFromComponent(title);
    let navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { theme, client, periodId, user, getAccessTokenSilently } = useVsiAppContext();
    const [isLoading, setIsLoading] = useState(true);

    // Grid
    const [gridData, setGridData] = useState([]);
    // Server By Type
    const [dbServerByTypePie, setDbServerPie] = useState([]);
    // Util By Type
    const [utilByTypePie, setUtilByTypePie] = useState([]);
    // Growth By Type
    const [growthByTypeData, setGrowthByTypeData] = useState([]);
    const [formattedTypeTrend, setFormattedGrowth] = useState([]);


    const setFuncs = {
        // Map procs w/ store-functions
        database_detail_proc: setGridData,
        database_growth_by_type_proc: setGrowthByTypeData,
    }


    const callReportProc = async function (endpoint) {
        const route = `/api/v6/report/${endpoint}?client_id=${client.clientId}&period_id=${periodId}`;
        try {
            const token = await getAccessTokenSilently();
            const data = await getReport(route, null, token);
            const narrowData = data?.data?.recordset;
            setFuncs[endpoint](narrowData || []);
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        callReportProc('database_detail_proc');
        callReportProc('database_growth_by_type_proc');
        setIsLoading(false);
    }, [client, periodId, searchParams]);

    const filterCalculationByType = (data) => {
        let utilPeiDict = {};
        let serverPeiDict = {};
        data.forEach(datum => {
            const dt = datum.type?.substring(0, 20);
            // Count number for each types
            serverPeiDict[dt] = serverPeiDict[dt] ? serverPeiDict[dt] + 1 : 1;
            // Calculate util
            if (utilPeiDict[dt]) {
                utilPeiDict[dt] += parseFloat(datum?.used) || 0;
            } else {
                utilPeiDict[dt] = parseFloat(datum?.used) || 0;
            }
        });
        const formattedServerPei = Object.entries(serverPeiDict).map(([name, y]) => ({name, y}));
        setDbServerPie(formattedServerPei);
        const formattedUtilPei = Object.entries(utilPeiDict).map(([name, y]) => ({name, y}));
        setUtilByTypePie(formattedUtilPei);
    };

    useEffect(() => {
        if (gridData.length) {
            filterCalculationByType(gridData);
        }
    }, [gridData]);

    useEffect(() => {
        let nameFieldsFound = {};
        const nameAndFields = [];
        growthByTypeData.forEach(datum => {
            if (!nameFieldsFound[datum.device_type_description]) {
                nameFieldsFound[datum.device_type_description] = true;
                nameAndFields.push({name: datum.device_type_description, field: 'used'});
            }
        });
        const allDbTrends = formatStockChartConfig(growthByTypeData, nameAndFields, 'device_type_description');
        setFormattedGrowth(allDbTrends.series);
    }, [growthByTypeData]);


    const onRowDoubleClick = (e) => {
        navigate(`/reports/database/server/database-instance-detail?instance_name=${encodeURI(e.data.instance)}&device_type=${encodeURI(e.data.device_type_code)}`)
    }


    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>Enterprise Databases Summary</VsiH4>
            <VsiBubble autobotName='DatabasePieChart' noAutobot>
                <Row>
                    <Col xs={12} lg={4}>
                        <Card color={theme}>
                            <CardHeader>
                                <VsiH5>{"Distribution by Server Type"}</VsiH5>
                            </CardHeader>
                            <CardBody>
                                <PieGrid
                                    data={dbServerByTypePie || []}
                                    gridConfig={["Database Type", "Count"]}
                                    colors={databaseTypesColors}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xs={12} lg={4}>
                        <Card color={theme}>
                            <CardHeader>
                                <VsiH5>{"Utilization Distribution by Server Type"}</VsiH5>
                            </CardHeader>
                            <CardBody>
                                <PieGrid
                                    data={utilByTypePie || []}
                                    gridConfig={["Database Type", "Used (GBs)"]}
                                    colors={databaseTypesColors}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xs={12} lg={4}>
                        <Stockchart
                            title={"Growth by Database Server Type"}
                            // height={chartHeight}
                            config={{
                                yAxis: {
                                    title: {
                                        text: '(GBs)'
                                    },
                                },
                                colors: databaseTypesColors
                            }}
                            series={formattedTypeTrend}
                        />
                    </Col>
                </Row>
            </VsiBubble>
            <VsiBubble>
                <Grid addStyle
                      gridText={'Double click a server to see more details'}
                      data={gridData || []}
                      isLoading={isLoading}
                      rowDoubleClicked={onRowDoubleClick}
                      columnDefs={enterpriseDatabaseSummaryDefs}/>
            </VsiBubble>
        </Card>
    );
}
